import { useTranslation } from "react-i18next"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import Tooltip from "@mui/material/Tooltip"
import { alpha } from "@mui/material/styles"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import type { FC } from "react"

import type { PowerlineSpanFeatureProps } from "types/geolocation.types"
import type { LastLimiterInfo, PeakSpanInfo } from "types/spans.types"

import SeverityDot from "widgets/plots/SeverityDot"
import { getNumericFormatter } from "helpers/formatters/plots"
import { formatTimestamp } from "helpers/utils/dateUtils"
import { getSpanDisplayName } from "helpers/utils/spans"
import useRoutes from "helpers/hooks/useRoutes"
import useContextDevices from "helpers/hooks/devices/useContextDevices"
import EntityPopup from "./EntityPopup"
import { getFeatureFields } from "./featureFields"

interface SpanPopupProps {
  data: PowerlineSpanFeatureProps
  lastLimiterAar?: LastLimiterInfo
  lastLimiterDlr?: LastLimiterInfo
  // Methods whose most frequent limiting span of the period is this one.
  peaks?: PeakSpanInfo[]
  // The line's spans in order, which an unnamed span is named by.
  lineSpanIds?: number[]
  hideButton?: boolean
}

const SpanPopup: FC<SpanPopupProps> = ({
  data,
  lastLimiterAar,
  lastLimiterDlr,
  peaks,
  lineSpanIds,
  hideButton,
}) => {
  const { i18n, t } = useTranslation()
  const { baseOrgURL } = useRoutes()
  const locale = i18n.resolvedLanguage ?? "en"
  const formatPct = getNumericFormatter(locale, 1)
  const { devices, isLoading: devicesLoading } = useContextDevices({
    asset: { entity: "span", id: data.id },
  })

  // One row per method rather than three near-identical branches for AAR, DLR
  // and both. Each carries its own timestamp: "last" needs a when.
  const lastLimiting = (
    [
      { key: "aar", color: "primary.main", limiter: lastLimiterAar },
      { key: "dlr", color: "secondary.main", limiter: lastLimiterDlr },
    ] as const
  ).filter((method) => method.limiter)

  // Tint the block with its hottest method. Seeded, so an empty list falls
  // through to the fallback instead of throwing.
  const peakAccent =
    peaks?.reduce<PeakSpanInfo | null>(
      (hottest, peak) => (!hottest || peak.pct > hottest.pct ? peak : hottest),
      null,
    )?.color ?? "transparent"

  const fields = getFeatureFields("PowerlineSpan", data, t)

  return (
    <EntityPopup
      entity="span"
      name={getSpanDisplayName(data, lineSpanIds, t)}
      fields={fields}
      id={data.id}
      devices={{ items: devices, loading: devicesLoading }}
      href={hideButton ? undefined : `${baseOrgURL}/spans/${data.id}`}
    >
      {lastLimiting.length > 0 && (
        <Stack
          spacing={0.75}
          sx={{
            backgroundColor: "warning.dark",
            border: "1px solid",
            borderColor: "warning.main",
            color: "text.primary",
            padding: 1,
            marginTop: 1.5,
            marginBottom: 1,
            borderRadius: 1,
          }}
        >
          {lastLimiting.map(({ key, color, limiter }, i) => (
            <Stack key={key} spacing={0.25}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Typography component="div" fontWeight="600">
                  {t("spans.ampacity.popup.lastLimiting")}{" "}
                  <Typography component="span" fontWeight="600" color={color}>
                    {key.toUpperCase()}
                  </Typography>
                </Typography>
                {/* One explanation for the block, not one per method. */}
                {i === 0 && (
                  <Tooltip
                    title={t("geo_info.LIMITING_SPAN_INFO")}
                    arrow
                    placement="bottom"
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 16, opacity: 0.7, flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
              </Stack>
              {limiter?.time && (
                <Typography variant="caption" sx={{ fontSize: "0.7rem", opacity: 0.9 }}>
                  {formatTimestamp(limiter.time)}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {!!peaks?.length && (
        <Stack
          spacing={0.5}
          sx={{
            // Same block as the notice above, in the frequency color.
            backgroundColor: alpha(peakAccent, 0.28),
            border: "1px solid",
            borderColor: peakAccent,
            color: "text.primary",
            padding: 1,
            marginTop: 1.5,
            marginBottom: 1,
            borderRadius: 1,
          }}
        >
          <Typography component="div" fontWeight="600">
            {t("spans.ampacity.popup.mostFrequent")}
          </Typography>
          {peaks.map((peak) => (
            <Stack
              key={peak.dataKey}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
            >
              <Typography variant="caption" fontWeight="600">
                {peak.dataKey.toUpperCase()}
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <SeverityDot color={peak.color} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t("spans.ampacity.hover.pctCount", {
                    pct: formatPct(peak.pct),
                    value: peak.count.toLocaleString(locale),
                  })}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </EntityPopup>
  )
}

export default SpanPopup
